import React, { useState } from 'react';
import { useGameStore } from '../../store/gameStore';
import { useTranslation } from '../../hooks/useTranslation';
import { BuildingModal } from './BuildingModal';
import { createFighter, getFighterSpritePath, getFighterMaxHealth, getFighterMaxStamina, Fighter } from '../../types/fighter';
import { WEAPONS, getWeaponById, Weapon } from '../../types/weapon';
import { motion } from 'framer-motion';

interface BarracksModalProps {
  onClose: () => void;
}

// Генерирует уникальное имя для бойца
const generateUniqueFighterName = (fighters: Fighter[], rank: number, getFighterRankName: (rank: number) => string): string => {
  const rankName = getFighterRankName(rank);
  
  // Находим максимальный номер для данного ранга
  const existingNames = fighters.map(f => f.name);
  let maxNumber = 0;
  
  for (const name of existingNames) {
    const match = name.match(new RegExp(`^${rankName} #(\\d+)$`));
    if (match) {
      const num = parseInt(match[1], 10);
      if (num > maxNumber) {
        maxNumber = num;
      }
    }
  }
  
  return `${rankName} #${maxNumber + 1}`;
};

export const BarracksModal: React.FC<BarracksModalProps> = ({ onClose }) => {
  const t = useTranslation();
  const {
    fighters,
    maxFighters,
    buildings,
    gold,
    resources,
    spendGold,
    addFighter,
    updateFighter,
    buildBuilding,
    weaponInventory,
    addWeaponToInventory,
    takeWeaponFromInventory
  } = useGameStore();

  const [isRecruiting, setIsRecruiting] = useState(false);
  const [selectedFighterId, setSelectedFighterId] = useState<string | null>(null);
  const [recruitConfig, setRecruitConfig] = useState<{
    leftHand: Weapon | null;
    rightHand: Weapon | null;
    armor: Weapon | null;
  }>({
    leftHand: getWeaponById(0) || null, // Дубина по умолчанию
    rightHand: null,
    armor: null
  });

  const selectedFighter = selectedFighterId ? fighters.find(f => f.id === selectedFighterId) : null;

  const handleBuild = () => {
    if (!buildings.barracks) {
      buildBuilding('barracks');
    }
  };

  const handleStartRecruitment = () => {
    if (fighters.length >= maxFighters) return;
    if (spendGold(20)) {
      setRecruitConfig({
        leftHand: getWeaponById(0) || null,
        rightHand: null,
        armor: null
      });
      setIsRecruiting(true);
      setSelectedFighterId(null);
    }
  };

  const handleSelectFighter = (fighterId: string) => {
    const fiber = fighters.find(f => f.id === fighterId);
    if (!fiber) return;
    setSelectedFighterId(fighterId);
    
    // Возвращаем текущее оружие бойца на склад (временно, для экипировки), кроме дубин
    if (fiber.leftHand && fiber.leftHand.id !== 0) {
      addWeaponToInventory(fiber.leftHand.id, 1);
    }
    if (fiber.rightHand && fiber.rightHand.id !== 0) {
      addWeaponToInventory(fiber.rightHand.id, 1);
    }
    if (fiber.armor && fiber.armor.id !== 0) {
      addWeaponToInventory(fiber.armor.id, 1);
    }
    
    // Показываем текущее оружие бойца в конфиге
    setRecruitConfig({
      leftHand: fiber.leftHand || null,
      rightHand: fiber.rightHand || null,
      armor: fiber.armor || null
    });
    setIsRecruiting(true);
  };

  const handleCancelRecruit = () => {
    // При отмене возвращаем оружие обратно на склад (если это существующий боец)
    if (selectedFighter) {
      if (selectedFighter.leftHand && selectedFighter.leftHand.id !== 0) {
        takeWeaponFromInventory(selectedFighter.leftHand.id, 1);
      }
      if (selectedFighter.rightHand && selectedFighter.rightHand.id !== 0) {
        takeWeaponFromInventory(selectedFighter.rightHand.id, 1);
      }
      if (selectedFighter.armor && selectedFighter.armor.id !== 0) {
        takeWeaponFromInventory(selectedFighter.armor.id, 1);
      }
    }
    
    // Возвращаем экипированное оружие на склад
    if (recruitConfig.leftHand && recruitConfig.leftHand.id !== 0) {
      addWeaponToInventory(recruitConfig.leftHand.id, 1);
    }
    if (recruitConfig.rightHand && recruitConfig.rightHand.id !== 0) {
      addWeaponToInventory(recruitConfig.rightHand.id, 1);
    }
    if (recruitConfig.armor && recruitConfig.armor.id !== 0) {
      addWeaponToInventory(recruitConfig.armor.id, 1);
    }
    
    setIsRecruiting(false);
    setSelectedFighterId(null);
    setRecruitConfig({
      leftHand: getWeaponById(0) || null,
      rightHand: null,
      armor: null
    });
  };

  const handleEquip = (weapon: Weapon) => {
    const isArmor = weapon.name.includes('armor');
    const isTwoHanded = weapon.cellsOccupied === 2;
    const isShield = weapon.attack === 0 && weapon.activeDefense > 0;
    const isWeapon = weapon.attack > 0;

    // Проверка на дублирование одноручного оружия
    if (isWeapon && !isTwoHanded) {
      if (recruitConfig.leftHand?.id === weapon.id || recruitConfig.rightHand?.id === weapon.id) {
        return; // Нельзя экипировать то же оружие дважды
      }
    }

    // Проверка на дублирование щитов
    if (isShield) {
      const hasShield = (recruitConfig.leftHand && recruitConfig.leftHand.attack === 0 && recruitConfig.leftHand.activeDefense > 0) ||
                       (recruitConfig.rightHand && recruitConfig.rightHand.attack === 0 && recruitConfig.rightHand.activeDefense > 0);
      if (hasShield) {
        return; // Нельзя экипировать два щита
      }
    }

    // Получаем старое оружие для возврата на склад
    const oldLeftHand = recruitConfig.leftHand;
    const oldRightHand = recruitConfig.rightHand;
    const oldArmor = recruitConfig.armor;

    let newConfig = { ...recruitConfig };

    if (isArmor) {
      newConfig.armor = weapon;
    } else {
      // Логика для рук
      if (isTwoHanded) {
        newConfig.leftHand = weapon;
        newConfig.rightHand = null;
      } else {
        // Если одноручное, пытаемся поставить в свободную руку или заменить
        if (!recruitConfig.leftHand || recruitConfig.leftHand.cellsOccupied === 2) {
          newConfig.leftHand = weapon;
        } else if (!recruitConfig.rightHand) {
          newConfig.rightHand = weapon;
        } else {
          // Заменяем в левой
          newConfig.leftHand = weapon;
        }
      }
    }

    // Обновляем склад сразу
    // Возвращаем старое оружие на склад (кроме дубин)
    if (oldLeftHand && oldLeftHand.id !== 0 && oldLeftHand.id !== newConfig.leftHand?.id && oldLeftHand.id !== newConfig.rightHand?.id) {
      addWeaponToInventory(oldLeftHand.id, 1);
    }
    if (oldRightHand && oldRightHand.id !== 0 && oldRightHand.id !== newConfig.leftHand?.id && oldRightHand.id !== newConfig.rightHand?.id) {
      addWeaponToInventory(oldRightHand.id, 1);
    }
    if (oldArmor && oldArmor.id !== 0 && oldArmor.id !== newConfig.armor?.id) {
      addWeaponToInventory(oldArmor.id, 1);
    }

    // Берем новое оружие со склада (кроме дубин)
    if (newConfig.leftHand && newConfig.leftHand.id !== 0 && newConfig.leftHand.id !== oldLeftHand?.id && newConfig.leftHand.id !== oldRightHand?.id) {
      const available = weaponInventory[newConfig.leftHand.id] || 0;
      if (available > 0) {
        takeWeaponFromInventory(newConfig.leftHand.id, 1);
      } else {
        return; // Недостаточно на складе
      }
    }
    if (newConfig.rightHand && newConfig.rightHand.id !== 0 && newConfig.rightHand.id !== oldLeftHand?.id && newConfig.rightHand.id !== oldRightHand?.id) {
      const available = weaponInventory[newConfig.rightHand.id] || 0;
      if (available > 0) {
        takeWeaponFromInventory(newConfig.rightHand.id, 1);
      } else {
        return; // Недостаточно на складе
      }
    }
    if (newConfig.armor && newConfig.armor.id !== 0 && newConfig.armor.id !== oldArmor?.id) {
      const available = weaponInventory[newConfig.armor.id] || 0;
      if (available > 0) {
        takeWeaponFromInventory(newConfig.armor.id, 1);
      } else {
        return; // Недостаточно на складе
      }
    }

    setRecruitConfig(newConfig);
  };

  const clearSlot = (slot: 'left' | 'right' | 'armor') => {
    const slotKey = slot === 'left' ? 'leftHand' : slot === 'right' ? 'rightHand' : 'armor';
    const oldWeapon = recruitConfig[slotKey];
    
    // Возвращаем оружие на склад (кроме дубин)
    if (oldWeapon && oldWeapon.id !== 0) {
      addWeaponToInventory(oldWeapon.id, 1);
    }
    
    setRecruitConfig(prev => ({ ...prev, [slotKey]: null }));
  };

  const handleFinalizeRecruit = () => {
    const currentConfig = {
      leftHand: recruitConfig.leftHand,
      rightHand: recruitConfig.rightHand,
      armor: recruitConfig.armor
    };

    // Склад уже обновлен в handleEquip и clearSlot, поэтому здесь только применяем изменения к бойцу

    if (selectedFighter) {
      // Рассчитываем новую максимальную стамину на основе веса экипировки
      let totalWeight = 0;
      if (currentConfig.leftHand) totalWeight += currentConfig.leftHand.weight;
      if (currentConfig.rightHand) totalWeight += currentConfig.rightHand.weight;
      if (currentConfig.armor) totalWeight += currentConfig.armor.weight;

      const baseStamina = 10 + selectedFighter.rank * 2;
      const newMaxStamina = Math.max(1, baseStamina - totalWeight);

      // Обновляем существующего бойца с новой экипировкой и стаминой
      updateFighter(selectedFighter.id, {
        leftHand: currentConfig.leftHand,
        rightHand: currentConfig.rightHand,
        armor: currentConfig.armor,
        maxStamina: newMaxStamina,
        stamina: Math.min(selectedFighter.stamina, newMaxStamina) // Текущая стамина не может превышать новый максимум
      });
    } else {
      // Создаем нового
      const uniqueName = generateUniqueFighterName(fighters, 0, t.getFighterRankName);
      const newFighter = createFighter(
        `fighter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        uniqueName,
        0,
        0,
        true
      );
      newFighter.leftHand = currentConfig.leftHand;
      newFighter.rightHand = currentConfig.rightHand;
      newFighter.armor = currentConfig.armor;
      addFighter(newFighter);
    }

    setIsRecruiting(false);
    setSelectedFighterId(null);
  };

  // Эмуляция бойца для получения спрайта
  const dummyFighter = {
    id: 'preview',
    name: 'Preview',
    health: 10,
    maxHealth: 10,
    stamina: 10,
    maxStamina: 10,
    leftHand: recruitConfig.leftHand,
    rightHand: recruitConfig.rightHand,
    armor: recruitConfig.armor,
    isDefending: false,
    x: 0,
    y: 0,
    isPlayer: true, // Важно: для людей всегда true
    rank: 0,
    rankPoints: 0
  } as Fighter;
  const currentSprite = getFighterSpritePath(dummyFighter);

  return (
    <BuildingModal
      title={t.modals.barracks.title}
      onClose={onClose}
      data-tutorial="barracksModal"
      content={
        <div className="text-white font-pixel text-xs min-h-[400px] overflow-x-hidden">
          {!buildings.barracks ? (
            <div className="flex flex-col items-center justify-center py-12">
              <img
                src="./sprites/buildings/barracks.png"
                alt={t.base.barracks}
                className="w-24 h-24 mb-6 object-contain filter drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]"
                onError={(e) => {
                  (e.target as any).style.display = 'none';
                  const el = (e.target as any).parentElement;
                  if (!el.getAttribute('data-has-emoji')) {
                    el.setAttribute('data-has-emoji', 'true');
                    el.insertAdjacentHTML('afterbegin', '<div class="text-6xl mb-6">⚔️</div>');
                  }
                }}
              />
              <div className="mb-6 text-gray-400 uppercase tracking-widest text-sm">{t.modals.barracks.notBuilt}</div>
              <button 
                data-tutorial="buildBarracksButton" 
                onClick={handleBuild}
                disabled={resources.metal < 50}
                style={{
                  backgroundColor: resources.metal >= 50 ? '#ca8a04' : '#374151',
                  color: resources.metal >= 50 ? '#000' : '#9ca3af',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  fontFamily: '"Press Start 2P", cursive, sans-serif',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: resources.metal >= 50 ? 'pointer' : 'not-allowed',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  boxShadow: resources.metal >= 50 ? '0 4px 0 #854d0e' : 'none',
                  transition: 'none'
                }}
                onMouseDown={(e) => {
                  if (resources.metal >= 50) {
                    e.currentTarget.style.transform = 'translateY(2px)';
                    e.currentTarget.style.boxShadow = '0 2px 0 #854d0e';
                  }
                }}
                onMouseUp={(e) => {
                  if (resources.metal >= 50) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 0 #854d0e';
                  }
                }}
              >
                {t.modals.barracks.build} {t.modals.barracks.buildCost}
              </button>
            </div>
          ) : isRecruiting ? (
            <div className="flex flex-col h-full gap-2 md:gap-4">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-yellow-900/40 p-2 md:p-3 border border-yellow-600/30 gap-2">
                <span className="uppercase tracking-tighter text-sm md:text-base break-words min-w-0">
                  {selectedFighter ? `${t.modals.barracks.equipment} ${selectedFighter.name}` : t.modals.barracks.newRecruit}
                </span>
                <button onClick={handleCancelRecruit} className="text-red-500 hover:text-red-400 text-sm md:text-base flex-shrink-0">{t.modals.barracks.cancel}</button>
              </div>

              <div className="flex flex-col md:flex-row flex-1 gap-2 md:gap-4 overflow-x-hidden">
                {/* СЛОТЫ ЭКИПИРОВКИ (СЛЕВА) */}
                <div data-tutorial="fighterEquipment" className="w-full md:w-32 flex flex-col gap-2 md:gap-3">
                  <div className="flex flex-row justify-between md:flex-col md:justify-start gap-2 md:gap-3">
                    {[
                      { label: t.modals.barracks.leftHand, slot: 'left', item: recruitConfig.leftHand },
                      { label: t.modals.barracks.armor, slot: 'armor', item: recruitConfig.armor },
                      { label: t.modals.barracks.rightHand, slot: 'right', item: recruitConfig.rightHand }
                    ].map((s) => (
                      <div key={s.slot} className="flex flex-col items-center gap-1 flex-shrink-0">
                        <div
                          onClick={() => clearSlot(s.slot as any)}
                          className="w-20 h-20 md:w-28 md:h-28 bg-black/60 border border-white/10 rounded flex items-center justify-center cursor-pointer hover:border-red-500/50 transition-colors group relative"
                        >
                          {s.item ? (
                            <>
                              <img
                                src={`./sprites/weapons/${s.item.name}.png`}
                                alt={t.getWeaponName(s.item.name)}
                                className="w-full h-full p-1 object-contain filter drop-shadow-[0_0_5px_rgba(255,215,0,0.3)]"
                                onError={(e) => {
                                  (e.target as any).style.display = 'none';
                                  const el = (e.target as any).parentElement;
                                  if (!el.getAttribute('data-has-emoji')) {
                                    el.setAttribute('data-has-emoji', 'true');
                                    el.insertAdjacentHTML('afterbegin', `<span class="text-4xl">${s.item!.name.includes('shield') ? '🛡️' : s.item!.name.includes('armor') ? '👕' : '🗡️'}</span>`);
                                  }
                                }}
                              />
                              <div className="absolute inset-0 bg-red-900/20 opacity-0 group-hover:opacity-100 flex items-center justify-center text-sm text-red-500">{t.modals.barracks.remove}</div>
                            </>
                          ) : (
                            <div className="text-xs md:text-sm text-white/20">{s.label}</div>
                          )}
                        </div>
                        {s.item && (
                          <div className="text-[10px] md:text-xs text-center px-1 break-words w-full text-white/70 max-w-[100px] md:max-w-none">
                            {t.getWeaponName(s.item.name)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* ПРЕВЬЮ ПЕРСОНАЖА (ЦЕНТР) */}
                <div className="flex-1 min-w-0 bg-black/40 border border-white/5 rounded flex flex-col items-center justify-center relative overflow-hidden p-1 md:p-4">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.05)_0%,transparent_70%)] pointer-events-none" />
                  <motion.img
                    key={currentSprite}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    src={currentSprite}
                    alt="Recruit Preview"
                    className="h-40 md:h-72 w-auto max-w-full object-contain drop-shadow-[0_0_20px_rgba(255,255,255,0.2)]"
                    onError={(e) => {
                      console.error('Failed to load sprite:', currentSprite);
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector('.sprite-fallback')) {
                        const fallback = document.createElement('div');
                        fallback.className = 'sprite-fallback text-6xl text-yellow-500';
                        fallback.textContent = '⚔️';
                        parent.appendChild(fallback);
                      }
                    }}
                  />
                  <div className="mt-1 md:mt-4 text-center px-2">
                    <div className="text-yellow-500 text-xs md:text-base mb-0.5 md:mb-1">{t.modals.barracks.ready}</div>
                    <div className="text-[10px] md:text-sm text-white/40 italic break-words">{t.modals.barracks.statsUpdate}</div>
                  </div>
                </div>

                {/* ИНВЕНТАРЬ (СПРАВА) */}
                <div data-tutorial="weaponStorage" className="w-full md:w-56 flex flex-col gap-1 md:gap-2 min-w-0">
                  <div className="text-xs md:text-sm text-gray-500 text-center uppercase mb-0.5 md:mb-1">{t.modals.barracks.weaponStorage}</div>
                  <div className="flex-1 overflow-y-auto pr-1 space-y-0.5 md:space-y-1 custom-scrollbar max-h-[180px] md:max-h-[300px]">
                    {WEAPONS.filter(weapon => weapon.id < 10).map(weapon => { // Фильтруем оружие орков (ID 10-19)
                      const count = weaponInventory[weapon.id] || 0;
                      // Дубина всегда доступна (бесплатно для новобранца и при переодевании)
                      const displayCount = weapon.id === 0 ? '∞' : count;

                      // Ограничение: Если это НОВЫЙ боец (найм) - только 0 ранг.
                      // Если уже существующий - ограничение по его текущему рангу.
                      const targetRank = selectedFighter ? selectedFighter.rank : 0;

                      const isRankAllowed =
                        (weapon.id === 0 || weapon.id === 7) ? targetRank >= 0 :
                          ([1, 3, 5, 8].includes(weapon.id)) ? targetRank >= 1 :
                            targetRank >= 2;

                      const isAvailable = (weapon.id === 0 || count > 0) && isRankAllowed;

                      return (
                        <button
                          key={weapon.id}
                          disabled={!isAvailable}
                          onClick={() => handleEquip(weapon)}
                          className={`w-full p-2 md:p-3 text-left border flex justify-between items-center transition-all min-w-0 ${isAvailable ? 'bg-dark-dirt border-white/10 hover:border-yellow-500' : 'bg-black/20 border-white/5 opacity-30 grayscale cursor-not-allowed'
                            }`}
                        >
                          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                            <div className="w-10 h-10 md:w-12 md:h-12 bg-black/40 border border-white/5 flex items-center justify-center rounded p-0.5 flex-shrink-0">
                              <img
                                src={`./sprites/weapons/${weapon.name}.png`}
                                alt={t.getWeaponName(weapon.name)}
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                  (e.target as any).style.display = 'none';
                                  const el = (e.target as any).parentElement;
                                  if (!el.getAttribute('data-has-emoji')) {
                                    el.setAttribute('data-has-emoji', 'true');
                                    el.insertAdjacentHTML('afterbegin', `<span class="text-lg">${weapon.name.includes('shield') ? '🛡️' : weapon.name.includes('armor') ? '👕' : '🗡️'}</span>`);
                                  }
                                }}
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs md:text-sm break-words text-yellow-500/90 font-bold">{t.getWeaponName(weapon.name)}</div>
                              <div data-tutorial="weaponStats" className="text-[10px] md:text-xs text-gray-400 flex flex-wrap gap-1 md:gap-2 items-center uppercase mt-1">
                                {weapon.attack > 0 ? (
                                  <><span>{t.modals.barracks.attack}{weapon.attack}</span><span>{t.modals.barracks.actionPoints}{weapon.actionPoints}</span><span>{t.modals.barracks.range}{weapon.range}</span></>
                                ) : (
                                  <><span>{t.modals.barracks.defense}{weapon.passiveDefense}/{weapon.activeDefense}</span></>
                                )}
                                <span>{t.modals.barracks.weight}{weapon.weight}</span>
                                {weapon.cellsOccupied === 2 && <span className="text-blue-400">{t.modals.barracks.twoHanded}</span>}
                              </div>
                              {!isRankAllowed && <div className="text-[10px] md:text-xs text-red-500 uppercase mt-1">{t.modals.barracks.rankRequired}</div>}
                            </div>
                          </div>
                          <div className={`text-sm md:text-base font-bold flex-shrink-0 ${isAvailable ? 'text-yellow-500' : 'text-gray-600'}`}>
                            {displayCount}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <button
                onClick={handleFinalizeRecruit}
                style={{
                  backgroundColor: '#ca8a04',
                  color: '#000',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  fontFamily: '"Press Start 2P", cursive, sans-serif',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  boxShadow: '0 4px 0 #854d0e',
                  transition: 'none',
                  width: '100%'
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'translateY(2px)';
                  e.currentTarget.style.boxShadow = '0 2px 0 #854d0e';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 0 #854d0e';
                }}
              >
                {selectedFighter ? t.modals.barracks.save : t.modals.barracks.finishRecruitment}
              </button>
            </div>
          ) : (
            <div className="flex flex-col bg-black/40 border border-white/5 p-3" style={{ minHeight: '700px' }}>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 flex-shrink-0" style={{ marginBottom: '16px' }}>
                <div className="min-w-0">
                  <div className="text-yellow-500 mb-1 uppercase tracking-widest text-base md:text-lg">{t.modals.barracks.squad}</div>
                  <div className="text-xs md:text-sm text-white/40">{t.modals.barracks.count} {fighters.length}/{maxFighters}</div>
                </div>
                <button
                  disabled={fighters.length >= maxFighters || gold < 20}
                  onClick={handleStartRecruitment}
                  style={{
                    backgroundColor: (fighters.length < maxFighters && gold >= 20) ? '#ca8a04' : '#374151',
                    color: (fighters.length < maxFighters && gold >= 20) ? '#000' : '#9ca3af',
                    padding: '0.5rem 1rem',
                    fontSize: '0.6rem',
                    fontWeight: 'bold',
                    fontFamily: '"Press Start 2P", cursive, sans-serif',
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: (fighters.length < maxFighters && gold >= 20) ? 'pointer' : 'not-allowed',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    boxShadow: (fighters.length < maxFighters && gold >= 20) ? '0 3px 0 #854d0e' : 'none',
                    transition: 'none',
                    flexShrink: 0
                  }}
                  onMouseDown={(e) => {
                    if (fighters.length < maxFighters && gold >= 20) {
                      e.currentTarget.style.transform = 'translateY(1.5px)';
                      e.currentTarget.style.boxShadow = '0 1.5px 0 #854d0e';
                    }
                  }}
                  onMouseUp={(e) => {
                    if (fighters.length < maxFighters && gold >= 20) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 3px 0 #854d0e';
                    }
                  }}
                >
                  {t.modals.barracks.recruit} {t.modals.barracks.recruitCost}
                </button>
              </div>

              <div className="space-y-3 overflow-y-auto pr-2 custom-scrollbar flex-1 min-h-0">
                {fighters.length === 0 ? (
                  <div className="text-center py-10 text-gray-700 italic text-base">{t.modals.barracks.empty}</div>
                ) : (
                  fighters.map((fighter) => (
                    <div key={fighter.id}
                      onClick={() => handleSelectFighter(fighter.id)}
                      className="bg-black/40 p-3 md:p-4 border border-white/10 rounded flex items-center gap-3 md:gap-6 cursor-pointer hover:border-yellow-500/50 transition-all group overflow-x-hidden"
                    >
                      <div className="relative flex-shrink-0">
                        <img 
                          src={getFighterSpritePath(fighter)} 
                          alt="Sprite" 
                          className="w-24 h-32 md:w-32 md:h-40 object-contain" 
                          onError={(e) => {
                            console.error('Failed to load fighter sprite:', getFighterSpritePath(fighter));
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent && !parent.querySelector('.sprite-fallback')) {
                              const fallback = document.createElement('div');
                              fallback.className = 'sprite-fallback text-4xl text-yellow-500 absolute inset-0 flex items-center justify-center';
                              fallback.textContent = '⚔️';
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                        <div className="absolute inset-0 bg-yellow-400/10 opacity-0 group-hover:opacity-100 flex items-center justify-center text-lg text-yellow-500 font-bold">⚙️</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-2 gap-1">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm md:text-lg text-white flex flex-wrap items-center gap-1 md:gap-2">
                              <span className="break-words">{fighter.name}</span>
                              <span className="text-xs md:text-sm px-1 md:px-2 py-0.5 bg-yellow-900/40 border border-yellow-600/30 text-yellow-500 rounded flex-shrink-0">
                                {t.modals.barracks.rank} {t.getFighterRankName(fighter.rank)}
                              </span>
                            </div>
                            <div data-tutorial="ranks" className="text-xs md:text-sm text-white/40 uppercase mt-1">
                              {t.modals.barracks.progress} {fighter.rankPoints}/{fighter.rank === 0 ? 3 : 9}
                            </div>
                          </div>
                          <div className={`text-sm md:text-base font-bold flex-shrink-0 ${fighter.health > 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {fighter.health > 0 ? t.modals.barracks.ready : t.modals.barracks.dead}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-1 md:gap-2 text-xs md:text-sm text-white/60 uppercase mb-2 md:mb-3">
                          <span className="break-words">{fighter.leftHand ? t.getWeaponName(fighter.leftHand.name) : t.common.no}</span>
                          <span className="opacity-30">|</span>
                          <span className="break-words">{fighter.armor ? t.getWeaponName(fighter.armor.name) : t.modals.barracks.armor}</span>
                          <span className="opacity-30">|</span>
                          <span className="break-words">{fighter.rightHand ? t.getWeaponName(fighter.rightHand.name) : t.common.no}</span>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2 md:gap-4">
                          {/* HP Bar */}
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between text-[10px] md:text-xs text-white/70 mb-1 uppercase font-bold">
                              <span>{t.modals.barracks.health}</span>
                              <span className="text-white">{fighter.health}/{getFighterMaxHealth(fighter)}</span>
                            </div>
                            <div className="h-1.5 w-full bg-white/5 border border-white/10 rounded-full overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${(fighter.health / getFighterMaxHealth(fighter)) * 100}%` }}
                                className={`h-full ${fighter.health < getFighterMaxHealth(fighter) * 0.3 ? 'bg-red-500' : 'bg-green-500'}`}
                              />
                            </div>
                          </div>
                          {/* Stamina Bar */}
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between text-[10px] md:text-xs text-white/70 mb-1 uppercase font-bold">
                              <span>{t.modals.barracks.stamina}</span>
                              <span className="text-yellow-500">{getFighterMaxStamina(fighter)}</span>
                            </div>
                            <div className="h-1.5 w-full bg-white/5 border border-white/10 rounded-full overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${(getFighterMaxStamina(fighter) / (10 + fighter.rank * 2)) * 100}%` }}
                                className="h-full bg-yellow-600"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      }
    />
  );
};
